import { EnabledDisabled } from "@/components/EnabledDisabled.tsx";
import S from "@/components/forms/Switch.module.css";
import { Spinner } from "@/components/Spinner.tsx";
import { raiseSavingChangesErrorToast } from "@/modules/notifications/toast.tsx";
import { cn, runIfFn } from "@/utils/classNames.ts";
import { usePromiseLoading } from "@/utils/react/usePromiseLoading.ts";
import { type ReactNode } from "react";
import { Switch, type SwitchProps } from "react-aria-components";

export interface WSwitchProps extends Pick<SwitchProps, "isSelected" | "isDisabled" | "className"> {
    onChange?: (isSelected: boolean) => void | Promise<void>;
    isLoading?: boolean;
    children?: ReactNode;
    side?: "left" | "right";
}

export function WSwitch({
    children,
    side = "left",
    className,
    isLoading: isLoadingProp,
    onChange,
    ...switchProps
}: WSwitchProps) {
    const { isPromiseLoading, trackPromise } = usePromiseLoading();
    const isLoading = isLoadingProp ?? isPromiseLoading;
    const indicator = <div className={S.indicator}>{isLoading && <Spinner headless />}</div>;

    return (
        <Switch
            {...switchProps}
            onChange={
                onChange && ((isSelected) => trackPromise(() => onChange(isSelected), raiseSavingChangesErrorToast))
            }
            isDisabled={isLoading || switchProps.isDisabled}
            className={(state) => cn(S.Switch, runIfFn(className, state))}
        >
            {side === "left" && indicator}
            <div>{children ?? <EnabledDisabled value={switchProps.isSelected ?? false} />}</div>
            {side === "right" && indicator}
        </Switch>
    );
}
